import React, { useMemo } from 'react';
import {
  GridComponent, ColumnsDirective, ColumnDirective, Inject, Sort,
  ColumnModel,
} from '@syncfusion/ej2-react-grids';
import { Box } from '@mui/material';
import VarianceChip, { FavorDirection } from './VarianceChip';
import { MonthlyComparisonView, VarianceDirection } from '../../../../services/LaborManagementService/types/laborManagementTypes';

interface BudgetVsActualsGridProps {
  months: MonthlyComparisonView[];
  lastCompletedMonth: number;
}

const currency = (value: number): string =>
  value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const decimal1 = (value: number): string => value.toFixed(1);

const BudgetVsActualsGrid: React.FC<BudgetVsActualsGridProps> = ({ months, lastCompletedMonth }) => {
  const { casesColumns, hoursColumns, costColumns, hpcColumns } = useMemo(() => {
    const mkVariance = (value: number | null, direction: VarianceDirection, favor: FavorDirection, month: number) =>
      month <= lastCompletedMonth
        ? <VarianceChip value={value} direction={direction} favorDirection={favor} />
        : null;

    return {
      casesColumns: [
        { field: 'budgetCases', headerText: 'Budget', width: '90', textAlign: 'Right', format: 'N0', headerTextAlign: 'Center' },
        { field: 'actualCases', headerText: 'Actual', width: '90', textAlign: 'Right', format: 'N0', headerTextAlign: 'Center' },
        { headerText: 'Var %', width: '120', textAlign: 'Center', headerTextAlign: 'Center',
          template: (row: MonthlyComparisonView) => mkVariance(row.casesVariancePercent, row.casesDirection, 'higher', row.month) },
      ] as ColumnModel[],
      hoursColumns: [
        { headerText: 'Budget', width: '90', textAlign: 'Right', headerTextAlign: 'Center',
          template: (row: MonthlyComparisonView) => decimal1(row.budgetDirectHours) },
        { headerText: 'Actual', width: '90', textAlign: 'Right', headerTextAlign: 'Center',
          template: (row: MonthlyComparisonView) => decimal1(row.actualDirectHours) },
        { headerText: 'Var %', width: '120', textAlign: 'Center', headerTextAlign: 'Center',
          template: (row: MonthlyComparisonView) => mkVariance(row.directHoursVariancePercent, row.directHoursDirection, 'lower', row.month) },
      ] as ColumnModel[],
      costColumns: [
        { headerText: 'Budget', width: '100', textAlign: 'Right', headerTextAlign: 'Center',
          template: (row: MonthlyComparisonView) => currency(row.budgetDirectLaborCost) },
        { headerText: 'Actual', width: '100', textAlign: 'Right', headerTextAlign: 'Center',
          template: (row: MonthlyComparisonView) => currency(row.actualDirectLaborCost) },
        { headerText: 'Var %', width: '120', textAlign: 'Center', headerTextAlign: 'Center',
          template: (row: MonthlyComparisonView) => mkVariance(row.directLaborCostVariancePercent, row.directLaborCostDirection, 'lower', row.month) },
      ] as ColumnModel[],
      hpcColumns: [
        { headerText: 'Budget', width: '90', textAlign: 'Right', headerTextAlign: 'Center',
          template: (row: MonthlyComparisonView) => decimal1(row.budgetHoursPerCase) },
        { headerText: 'Actual', width: '90', textAlign: 'Right', headerTextAlign: 'Center',
          template: (row: MonthlyComparisonView) => decimal1(row.actualHoursPerCase) },
        { headerText: 'Var %', width: '120', textAlign: 'Center', headerTextAlign: 'Center',
          template: (row: MonthlyComparisonView) => mkVariance(row.hoursPerCaseVariancePercent, row.hoursPerCaseDirection, 'lower', row.month) },
      ] as ColumnModel[],
    };
  }, [lastCompletedMonth]);

  return (
    <Box sx={{
      '& .e-grid': { border: 'none' },
      '& .e-gridheader': { borderBottom: '2px solid #e2e8f0' },
      '& .e-stackedheadercelldiv': { fontWeight: 600, fontSize: '0.8rem', textAlign: 'center' },
    }}>
      <GridComponent
        key={`${lastCompletedMonth}-${months.length}-${months[0]?.monthName ?? ''}`}
        dataSource={months}
        allowPaging={false}
        allowSorting={true}
        height="auto"
      >
        <ColumnsDirective>
          <ColumnDirective
            field="monthName"
            headerText="Month"
            width="100"
            textAlign="Left"
            isPrimaryKey={true}
          />
          <ColumnDirective headerText="Cases" columns={casesColumns} headerTextAlign="Center" />
          <ColumnDirective headerText="Direct Hours" columns={hoursColumns} headerTextAlign="Center" />
          <ColumnDirective headerText="Labor Cost" columns={costColumns} headerTextAlign="Center" />
          <ColumnDirective headerText="Hours / Case" columns={hpcColumns} headerTextAlign="Center" />
        </ColumnsDirective>
        <Inject services={[Sort]} />
      </GridComponent>
    </Box>
  );
};

export default BudgetVsActualsGrid;
